import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  LogOut,
  Settings,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { navSections, type Section } from '@/lib/sections';
import { useSidebar } from '@/hooks/useSidebar';
import { useAuth } from '@/contexts/AuthContext';
import { spacing } from '@/design/tokens';

export function Sidebar() {
  const { collapsed, toggle } = useSidebar();
  const location = useLocation();
  const { user, signOut, canAccessSection } = useAuth();

  const width = collapsed ? spacing.sidebar.collapsed : spacing.sidebar.expanded;
  const visibleSections = navSections.filter((s) => canAccessSection(s.id));

  return (
    <motion.aside
      className="fixed left-0 top-0 bottom-0 z-40 flex flex-col bg-navy-900 text-white overflow-hidden"
      animate={{ width }}
      transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
    >
      {/* Logo / Brand */}
      <div className="flex items-center h-14 px-5 shrink-0 border-b border-white/[0.06]">
        <NavLink to="/" className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-brand-500 flex items-center justify-center shrink-0">
            <LayoutDashboard className="w-4.5 h-4.5 text-white" />
          </div>
          <AnimatePresence>
            {!collapsed && (
              <motion.span
                className="text-sm font-semibold text-white truncate whitespace-nowrap"
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
              >
                Product Dashboard
              </motion.span>
            )}
          </AnimatePresence>
        </NavLink>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-3 scrollbar-hide">
        <div className="space-y-0.5">
          {visibleSections.map((section) => (
            <SidebarItem
              key={section.id}
              section={section}
              collapsed={collapsed}
              isActive={location.pathname.startsWith(section.route)}
            />
          ))}
        </div>
      </nav>

      {/* Settings (bottom) */}
      {canAccessSection('settings') && (
        <div className="px-3 pb-2 border-t border-white/[0.06] pt-2">
          <SidebarItem
            section={{
              id: 'settings',
              name: 'Settings',
              description: '',
              icon: Settings,
              route: '/settings',
              enabled: false,
              accent: '#64748B',
            }}
            collapsed={collapsed}
            isActive={location.pathname === '/settings'}
          />
        </div>
      )}

      {/* User / Sign Out */}
      {user && (
        <div className="px-3 pb-2 border-t border-white/[0.06] pt-2">
          <button
            onClick={signOut}
            className={cn(
              'group relative flex items-center gap-3 px-3 h-10 rounded-lg w-full',
              'text-slate-400 hover:text-white hover:bg-white/[0.06]',
              'transition-all duration-150 text-sm',
            )}
            title="Sign out"
          >
            {user.photoURL ? (
              <img
                src={user.photoURL}
                alt=""
                className="w-[18px] h-[18px] rounded-full shrink-0"
                referrerPolicy="no-referrer"
              />
            ) : (
              <LogOut className="w-[18px] h-[18px] shrink-0" />
            )}
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  className="truncate whitespace-nowrap font-medium"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {user.displayName?.split(' ')[0] ?? 'Sign out'}
                </motion.span>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  className="ml-auto"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <LogOut className="w-3.5 h-3.5 text-slate-500 group-hover:text-slate-300" />
                </motion.span>
              )}
            </AnimatePresence>
            {/* Tooltip when collapsed */}
            {collapsed && (
              <div
                className={cn(
                  'absolute left-full ml-2 px-2.5 py-1.5 rounded-lg',
                  'bg-slate-800 text-white text-xs font-medium whitespace-nowrap',
                  'opacity-0 pointer-events-none group-hover:opacity-100',
                  'transition-opacity duration-150 z-50',
                  'shadow-lg',
                )}
              >
                Sign out ({user.email})
              </div>
            )}
          </button>
        </div>
      )}

      {/* Collapse Toggle */}
      <button
        onClick={toggle}
        className={cn(
          'flex items-center justify-center h-10 mx-3 mb-3 rounded-lg',
          'text-slate-400 hover:text-white hover:bg-white/[0.06]',
          'transition-colors duration-150',
        )}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <ChevronRight className="w-4 h-4" />
        ) : (
          <ChevronLeft className="w-4 h-4" />
        )}
      </button>
    </motion.aside>
  );
}

// ─── Sidebar Item ───────────────────────────────────────────────────────────

function SidebarItem({
  section,
  collapsed,
  isActive,
}: {
  section: Section;
  collapsed: boolean;
  isActive: boolean;
}) {
  const Icon = section.icon;

  return (
    <NavLink
      to={section.enabled ? section.route : '#'}
      className={cn(
        'group relative flex items-center gap-3 px-3 h-10 rounded-lg',
        'transition-all duration-150 text-sm',
        isActive
          ? 'bg-white/[0.1] text-white'
          : 'text-slate-400 hover:text-white hover:bg-white/[0.06]',
        !section.enabled && 'opacity-40 pointer-events-none',
      )}
      onClick={(e) => {
        if (!section.enabled) e.preventDefault();
      }}
    >
      {/* Active indicator */}
      {isActive && (
        <motion.div
          layoutId="sidebar-active"
          className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-brand-400"
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        />
      )}

      <Icon className="w-[18px] h-[18px] shrink-0" />

      <AnimatePresence>
        {!collapsed && (
          <motion.span
            className="truncate whitespace-nowrap font-medium"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            transition={{ duration: 0.2 }}
          >
            {section.name}
          </motion.span>
        )}
      </AnimatePresence>

      {/* Tooltip when collapsed */}
      {collapsed && (
        <div
          className={cn(
            'absolute left-full ml-2 px-2.5 py-1.5 rounded-lg',
            'bg-slate-800 text-white text-xs font-medium whitespace-nowrap',
            'opacity-0 pointer-events-none group-hover:opacity-100',
            'transition-opacity duration-150 z-50',
            'shadow-lg',
          )}
        >
          {section.name}
          {!section.enabled && (
            <span className="ml-1.5 text-slate-400">(coming soon)</span>
          )}
        </div>
      )}
    </NavLink>
  );
}
